import { z } from 'zod';
export const DROITS_METIER = [
    'vente',
    'encaissement',
    'remboursement',
    'cloture',
    'export_nominatif',
    'configuration',
    'controle',
    'comptabilite',
    'personnel',
    'planning',
    'communication',
];
export const LIBELLES_DROITS_METIER = {
    vente: 'Ventes et souscriptions',
    encaissement: 'Encaissements et corrections de solde',
    remboursement: 'Annulations et remboursements',
    cloture: 'Clôture des caisses',
    export_nominatif: 'Exports et documents nominatifs',
    configuration: 'Configuration et catalogue',
    controle: 'Contrôle des entrées',
    comptabilite: 'Banque et journal comptable',
    personnel: 'Gestion du personnel',
    planning: 'Occupation et planning des lieux',
    communication: 'Communication et CRM',
};
export const droitsMetierSchema = z
    .array(z.enum(DROITS_METIER))
    .max(DROITS_METIER.length)
    .refine((v) => new Set(v).size === v.length, {
    message: 'Chaque droit ne peut être choisi qu’une fois.',
})
    .nullable();
/** Une restriction ne donne jamais un rôle supplémentaire. null conserve les gardes du rôle. */
export function peutExercer(compte, droit) {
    return compte.droitsMetier == null || compte.droitsMetier.includes(droit);
}
const ecriture = (methode) => !['GET', 'HEAD', 'OPTIONS'].includes(methode);
const exportDocument = /(?:\.pdf|\.csv|\/pdf|\/csv|\/export(?:s)?|\/envoyer|\/renvoyer)(?:\/|$)/u;
const REGLES_ECRITURE = [
    [/^\/(?:ventes|souscriptions|billets)\/[^/]+\/annuler/u, ['vente', 'remboursement']],
    [/^\/representations\/[^/]+\/annuler/u, ['configuration', 'remboursement']],
    [/^\/encaissements\/[^/]+\/contrepasser/u, ['encaissement', 'remboursement']],
    [/\/(?:rembourser|contrepasser|annuler|executer)(?:\/|$)/u, ['remboursement']],
    [/^\/avoirs(?:\/|$)/u, ['remboursement']],
    [/^\/caisses\/sessions\/[^/]+\/fermer$/u, ['cloture']],
    [/^\/caisses(?:\/|$)/u, ['encaissement']],
    [/^\/facturation(?:\/|$)/u, ['comptabilite']],
    [
        /^\/(?:paiements|encaissements|deductions|ajustements|factures|relances)(?:\/|$)/u,
        ['encaissement'],
    ],
    [/\/(?:paiement|pointer)$/u, ['encaissement']],
    [/^\/(?:ventes|paniers|souscriptions|personnes|billets|cartes)(?:\/|$)/u, ['vente']],
    [
        /^\/(?:theatre|salles|sieges|zones|categories-public|types-deduction|saisons|spectacles|representations|grilles|tarifs|formules|campagnes|incidents|deblocage)(?:\/|$)/u,
        ['configuration'],
    ],
];
function droitsEcriture(route, corps) {
    if (/^\/paniers\/[^/]+\/confirmer$/u.test(route))
        return corps?.modeEncaissement === 'facture' ? ['vente'] : ['vente', 'encaissement'];
    if (/^\/(?:ventes|souscriptions)(?:\/|$)/u.test(route) && corps?.statutPaiement === 'paye')
        return ['vente', 'encaissement'];
    return REGLES_ECRITURE.find(([motif]) => motif.test(route))?.[1] ?? null;
}
/** Les opérations sont classées une seule fois. Une mutation nouvelle non classée
 * est refusée aux comptes restreints, même si leur rôle serait administrateur. */
export function droitsPourRequete(methode, chemin, corps) {
    const route = chemin.replace(/^\/api/u, '').split('?')[0] ?? '';
    const regle = REGLES_REQUETE.find(([motif]) => motif.test(route));
    if (regle)
        return regle[1](methode, route);
    return ecriture(methode) ? droitsEcriture(route, corps) : [];
}
/** Les écrans de consultation restent bornés par les rôles existants. */
export function peutOuvrirEcran(compte, chemin) {
    if (compte.droitsMetier == null)
        return true;
    const route = chemin.replace(/^\/admin/u, '');
    return REGLES_ECRAN.find(([motif]) => motif.test(route))?.[1](compte) ?? true;
}
const REGLES_REQUETE = [
    [/^\/acces\/(?:[^/]+\/)?accreditations(?:\/|$|\.pdf)/u, () => ['configuration', 'controle', 'export_nominatif']],
    [/^\/acces(?:\/|$)/u, () => ['configuration', 'controle']],
    [/^\/scan-avance(?:\/|$)/u, () => ['controle']],
    [/^\/distribution\/politique(?:\/|$)/u, () => ['configuration']],
    [/^\/distribution(?:\/|$)/u, () => ['vente', 'export_nominatif']],
    [/^\/crm(?:\/|$)/u, () => ['communication', 'export_nominatif']],
    [/^\/communications\/desinscription$/u, () => []],
    [/^\/pilotage(?:\.csv)?$/u, () => ['comptabilite']],
    [/^\/partenaire(?:\/|$)/u, () => []],
    [/^\/partenaires\/[^/]+\/(?:releves|commandes)(?:\/|$)/u, () => ['comptabilite', 'export_nominatif']],
    [/^\/partenaires(?:\/|$)/u, () => ['configuration']],
    [/^\/wallet\/document$/u, () => ['export_nominatif']],
    [/^\/impressions\/politique$/u, (m) => (ecriture(m) ? ['configuration'] : [])],
    [/^\/impressions(?:\/|$)/u, () => ['vente', 'export_nominatif']],
    [/^\/(?:auth|console|portail|inscription)(?:\/|$)/u, () => []],
    [/^\/boutique(?:\/|$)/u, (methode) => (ecriture(methode) ? ['vente'] : [])],
    [/^\/promotions(?:\/|$)/u, () => ['configuration']],
    [/^\/paniers\/[^/]+\/promotions(?:\/|$)/u, () => ['vente']],
    [/^\/bons\/politique$/u, () => ['configuration']],
    [
        /^\/bons(?:\/|$)/u,
        (_m, r) => r.includes('convertir-avoir') || /\/(?:retour-source|restituer)$/u.test(r)
            ? ['encaissement', 'remboursement', 'export_nominatif']
            : ['encaissement', 'export_nominatif'],
    ],
    [/^\/finance\/delegations(?:\/|$)/u, () => ['personnel']],
    [/^\/finance(?:\/|$)/u, () => []],
    [/^\/planning(?:\/|$)/u, (methode) => (ecriture(methode) ? ['planning'] : [])],
    [/^\/demandes-spectateurs\/politique$/u, () => ['configuration']],
    [/^\/demandes-spectateurs(?:\/|$)/u, () => ['vente', 'export_nominatif']],
    [/^\/attente(?:\/|$)/u, () => ['vente', 'export_nominatif']],
    [
        /^\/groupes(?:\/|$)/u,
        (_m, route) => route.endsWith('/devis.pdf') || route.endsWith('/envoyer')
            ? ['vente', 'export_nominatif']
            : ['vente'],
    ],
    [
        /^\/secours(?:\/|$)/u,
        (_m, route) => exportDocument.test(route)
            ? ['vente', 'encaissement', 'export_nominatif']
            : ['vente', 'encaissement'],
    ],
    [/^\/scan\/affectations(?:\/|$)/u, () => ['personnel']],
    [
        /^\/scan(?:\/|$)/u,
        (_m, route) => (exportDocument.test(route) ? ['controle', 'export_nominatif'] : ['controle']),
    ],
    [/^\/(?:comptabilite|banque)(?:\/|$)/u, () => ['comptabilite']],
    [/^\/cartes\/[^/]+\/regenerer$/u, () => ['vente', 'export_nominatif']],
    [exportDocument, () => ['export_nominatif']],
    [/^\/utilisateurs(?:\/|$)/u, () => ['personnel']],
];
const REGLES_ECRAN = [
    [/^\/acces(?:\/|$)/u, (c) => peutExercer(c, 'configuration') && peutExercer(c, 'controle')],
    [/^\/distribution(?:\/|$)/u, (c) => peutExercer(c, 'vente') && peutExercer(c, 'export_nominatif')],
    [/^\/crm(?:\/|$)/u, (c) => peutExercer(c, 'communication') && peutExercer(c, 'export_nominatif')],
    [/^\/pilotage$/u, (c) => peutExercer(c, 'comptabilite')],
    [
        /^\/ventes\/impressions(?:\/|$)/u,
        (c) => peutExercer(c, 'vente') && peutExercer(c, 'export_nominatif'),
    ],
    [/^\/ventes\/promotions(?:\/|$)/u, (c) => peutExercer(c, 'configuration')],
    [
        /^\/finance(?:\/|$)/u,
        (c) => ['encaissement', 'remboursement', 'cloture', 'personnel'].some((d) => peutExercer(c, d)),
    ],
    [/^\/planning(?:\/|$)/u, () => true],
    [
        /^\/configuration(?:\/|$)/u,
        (c) => peutExercer(c, 'configuration') || peutExercer(c, 'personnel'),
    ],
    [/^\/paiements\/(?:comptabilite|rapprochement)(?:\/|$)/u, (c) => peutExercer(c, 'comptabilite')],
    [
        /^\/paiements\/caisse(?:\/|$)/u,
        (c) => peutExercer(c, 'cloture') || peutExercer(c, 'encaissement'),
    ],
    [
        /^\/ventes\/demandes(?:\/|$)/u,
        (c) => peutExercer(c, 'vente') && peutExercer(c, 'export_nominatif'),
    ],
    [
        /^\/ventes\/attente(?:\/|$)/u,
        (c) => peutExercer(c, 'vente') && peutExercer(c, 'export_nominatif'),
    ],
    [
        /^\/(?:ventes\/)?secours(?:\/|$)/u,
        (c) => peutExercer(c, 'vente') && peutExercer(c, 'encaissement'),
    ],
    [/^\/scan(?:\/|$)/u, (c) => peutExercer(c, 'controle')],
    [
        /^\/paiements(?:\/|$)/u,
        (c) => peutExercer(c, 'encaissement') || peutExercer(c, 'remboursement'),
    ],
    [/^\/(?:ventes|paniers|souscriptions|personnes)(?:\/|$)/u, (c) => peutExercer(c, 'vente')],
    [
        /^\/(?:apparence|catalogue|tarifs|tarification|regie|formules|campagne|deblocage)(?:\/|$)/u,
        (c) => peutExercer(c, 'configuration'),
    ],
];
//# sourceMappingURL=permissions.js.map